import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { StatusDot } from '../../components/ui/StatusDot';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioMedidores,
  type NumeroRele,
} from '../../../data/repositories/RepositorioMedidores';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import type { Medidor } from '../../../domain/entities/Medidor';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { useSesionStore } from '../../stores/sesionStore';
import { formatearAntiguedad } from '../../utils/formato';
import { DetalleMedidor } from './DetalleMedidor';
import estilos from './Energia.module.css';

type FiltroEstado = 'todos' | 'ok' | 'error' | 'sin_lectura';

const OPCIONES_ESTADO = [
  { valor: 'todos', etiqueta: 'Todos' },
  { valor: 'ok', etiqueta: 'Leyendo bien' },
  { valor: 'error', etiqueta: 'Con error' },
  { valor: 'sin_lectura', etiqueta: 'Sin lecturas' },
];

function formatear(valor: number | null, decimales = 2): string {
  return valor === null ? '—' : valor.toFixed(decimales);
}

export function Energia() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);

  const [clienteId, setClienteId] = useState<number | null>(null);
  const [estado, setEstado] = useState<FiltroEstado>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [releEnCurso, setReleEnCurso] = useState<NumeroRele | null>(null);

  const consulta = useQuery({
    queryKey: ['medidores', clienteId],
    queryFn: () => repositorioMedidores.listar(clienteId),
  });

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const refrescar = useCallback(() => {
    void clienteConsultas.invalidateQueries({ queryKey: ['medidores'] });
  }, [clienteConsultas]);

  // Cada lectura nueva de cualquier medidor refresca la pantalla.
  useTiempoReal({ tabla: 'last_meter_values', evento: '*' }, refrescar);

  const medidores = useMemo(() => consulta.data ?? [], [consulta.data]);

  const filtrados = useMemo(
    () =>
      medidores.filter((m) => {
        if (!m.coincideCon(busqueda)) return false;
        if (estado === 'ok') return m.lecturaCorrecta === true;
        if (estado === 'error') return m.lecturaCorrecta === false;
        if (estado === 'sin_lectura') return m.lecturaCorrecta === null;
        return true;
      }),
    [medidores, busqueda, estado],
  );

  const detalle = useMemo(
    () => medidores.find((m) => m.id === detalleId) ?? null,
    [medidores, detalleId],
  );

  const accionarRele = useMutation({
    mutationFn: ({ rele, encender }: { rele: NumeroRele; encender: boolean }) => {
      if (detalleId === null) return Promise.resolve();
      setReleEnCurso(rele);
      return repositorioMedidores.accionarRele(detalleId, rele, encender);
    },
    onSuccess: (_, { rele, encender }) => {
      toast.success(`Relé ${rele} ${encender ? 'cerrado' : 'abierto'}`);
      refrescar();
    },
    onError: (e) => {
      registrarError('energia.accionarRele', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo accionar el relé');
    },
    onSettled: () => setReleEnCurso(null),
  });

  const puedeAccionar = politica?.puedeOperarActuadores ?? false;

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Energía"
        descripcion="Consumo eléctrico y control de los medidores."
        acciones={
          <Button
            variante="secundario"
            icono="refrescar"
            compactoEnMovil
            onClick={refrescar}
            cargando={consulta.isFetching}
          >
            Actualizar
          </Button>
        }
      />

      <BarraFiltros>
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Nombre, serial o cliente"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={clienteId ?? ''}
          onChange={(e) => setClienteId(e.target.value ? Number(e.target.value) : null)}
          opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />
        <Select
          etiqueta="Estado de lectura"
          value={estado}
          onChange={(e) => setEstado(e.target.value as FiltroEstado)}
          opciones={OPCIONES_ESTADO}
        />
      </BarraFiltros>

      {consulta.isLoading ? (
        <ListaEsqueleto filas={4} />
      ) : consulta.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="medidor"
          titulo={medidores.length === 0 ? 'Sin medidores' : 'Ningún medidor coincide'}
          descripcion={
            medidores.length === 0
              ? 'Cuando se configure un medidor de energía aparecerá aquí.'
              : 'Prueba a cambiar los filtros para ver más resultados.'
          }
        />
      ) : (
        <div className={estilos.rejilla}>
          {filtrados.map((m: Medidor) => (
            <button
              key={m.id}
              type="button"
              className={estilos.tarjeta}
              onClick={() => setDetalleId(m.id)}
            >
              <div className={estilos.cabecera}>
                <div style={{ minWidth: 0 }}>
                  <div className={estilos.nombre}>{m.nombre}</div>
                  <div className={estilos.serial}>
                    {m.serial}
                    {m.clienteNombre ? ` · ${m.clienteNombre}` : ''}
                  </div>
                </div>
                <Badge color="var(--text-2)">{m.protocolo}</Badge>
              </div>

              <div className={estilos.magnitudes}>
                <div className={estilos.magnitud}>
                  <div className={estilos.magnitudEtiqueta}>{m.tipoSuministroEtiqueta}</div>
                  <div className={estilos.magnitudValor}>
                    {formatear(m.tensionMostrable, 1)}
                    <span className={estilos.magnitudUnidad}>V</span>
                  </div>
                </div>

                <div className={estilos.magnitud}>
                  <div className={estilos.magnitudEtiqueta}>Corriente</div>
                  <div className={estilos.magnitudValor}>
                    {m.corrientesActivas.length > 0
                      ? m.corrientesActivas.map((a) => a.toFixed(2)).join(' · ')
                      : '—'}
                    <span className={estilos.magnitudUnidad}>A</span>
                  </div>
                </div>

                <div className={estilos.magnitud}>
                  <div className={estilos.magnitudEtiqueta}>Potencia</div>
                  <div className={estilos.magnitudValor}>
                    {formatear(m.potenciaTotal, 0)}
                    <span className={estilos.magnitudUnidad}>W</span>
                  </div>
                </div>

                <div className={estilos.magnitud}>
                  <div className={estilos.magnitudEtiqueta}>Energía</div>
                  <div className={estilos.magnitudValor}>
                    {formatear(m.energiaTotal, 1)}
                    <span className={estilos.magnitudUnidad}>kWh</span>
                  </div>
                </div>
              </div>

              <div className={estilos.pie}>
                <StatusDot
                  color={
                    m.lecturaCorrecta === null
                      ? 'var(--text-3)'
                      : m.lecturaCorrecta
                        ? 'var(--status-success)'
                        : 'var(--status-danger)'
                  }
                  encendido={m.lecturaCorrecta !== null}
                  texto={
                    m.lecturaCorrecta === null
                      ? 'Sin lectura'
                      : m.lecturaCorrecta
                        ? 'Leyendo'
                        : 'Error'
                  }
                  tamano={8}
                />
                <span>{formatearAntiguedad(m.ultimaLectura)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <DetalleMedidor
        medidor={detalle}
        onCerrar={() => setDetalleId(null)}
        puedeAccionar={puedeAccionar}
        motivoSinPermiso={politica?.motivoDenegacion('operar_actuadores') ?? null}
        onAccionarRele={(rele, encender) => accionarRele.mutate({ rele, encender })}
        releEnCurso={releEnCurso}
      />
    </div>
  );
}
